/**
 * Extracts subject and body from an HTML string formatted as follows:
 *  Subject RegEx: Match ONLY if <p>Subject: ...</p> is at the start of the HTML,
 *   start of string (ensures it’s the *first* element)
 *   [\s\S]*? allows flexible text until </p>
 *  Body RegEx: Everything after the subject line, removing leading empty paragraphs.
 * 
 * Expected input format:
 *   Example full HTML:
 *     <p>Subject: Enhance Your Content Management with Paperflite</p>
 *     <p>&nbsp;</p> <p>&nbsp;</p>
 *     <p>Dear [Prospect's Name],</p>
 *     <p>I hope this message finds you well...</p>
 *     <p>&nbsp;</p>
 *
 * Output:
 *   {
 *     subject: "Enhance Your Content Management with Paperflite",
 *     body: "<p>Dear [Prospect's Name],</p><p>I hope this message finds you well...</p> <p>&nbsp;</p>"
 *   }
 *
 * Behavior:
 *   1. Looks for the first <p> tag that starts with "Subject:" (case-insensitive).
 *   2. Extracts only the text following "Subject:" inside that <p>.
 *   3. Removes that subject paragraph from the HTML string.
 *   4. Removes any leading empty <p> tags (like <p>&nbsp;</p> or whitespace-only <p>) before the actual body starts.
 *   5. Returns both `subject` (as plain text) and `body` (as cleaned HTML).
 *
 * Notes:
 *   - If no <p>Subject:</p> tag is found, the entire HTML is treated as body.
 *   - The function safely ignores extra spacing or HTML formatting differences.
 * 
 * Invalid example (will return empty subject and full body):
 * <p>Hello team,</p>
 * <p>Here is our update.</p>
 * <p>Subject: Random note</p>
 *  <p>Regards,</p>
 */
export function parseEmailSubjectAndBody(htmlString: string) {
  if (!htmlString) return { subject: "", body: "" }; // handle empty input

  const subjectRegex = /^\s*<p[^>]*>\s*Subject:\s*([\s\S]*?)<\/p>/i;
  const subjectFound = htmlString.match(subjectRegex);   // find the subject line

  if (!subjectFound) { // no subject found
    return { subject: "", body: htmlString };
  }

  const subject = subjectFound[1].trim(); // subject text

  let body = htmlString.replace(subjectRegex, ""); //  body extraction (remove subject line from original string)

  // remove any empty spaces before the actual body content
  const emptyParaRegex = /^\s*<p>(?:&nbsp;|\s)*<\/p>\s*/i;
  while (emptyParaRegex.test(body)) { // loop continues stripping <p>&nbsp;</p> from the start of the body
    body = body.replace(emptyParaRegex, "");
  }

  return { subject, body: body.trim() };
}